import { baseApi } from './baseApi';

export type ErrorSeverity = 'crash' | 'degraded' | 'expected';

export interface DashboardData {
  range: { from: string; to: string };
  totals: {
    total: number;
    /** Errores accionables (crash + degraded). Los 'expected' no se cuentan acá. */
    errors: number;
    errorsBySeverity: { crash: number; degraded: number; expected: number };
    /** Total bruto de eventos type='error' (incluye los esperados). */
    errorsRaw: number;
    gustavo: number;
    uniqueAgents: number; uniqueVisitors: number;
  };
  byDay: Array<{ date: string; total: number; agents: number; clients: number }>;
  byCategory: Array<{ category: string; count: number }>;
  topPaths:   Array<{ path: string;   count: number }>;
  topActions: Array<{ action: string; count: number }>;
}

export interface PropertyView {
  propertyId: string;
  title: string;
  views: number;
  uniqueVisitors: number;
  link: string;
}

export interface GustavoTool {
  tool: string;
  count: number;
  uniqueUsers: number;
}

export interface AgentActivity {
  userId: string;
  name: string;
  email: string | null;
  role: string | null;
  events: number;
  actions: number;
  gustavo: number;
  lastSeen: string;
  firstSeen: string;
}

export type RangeParam = '24h' | '7d' | '30d' | '90d';

/** Rotación de agentes y quién hace los negocios (Soporte › Analítica). */
export interface ImpactoPorTipo { total: number; veteranos: number; nuevos: number; yaNoEstan: number; pctVeteranos: number }
export interface RotacionAgentes {
  desde: string;
  plantel: {
    alInicio: number; hoy: number; altas: number; bajas: number; bajasQueEntraronEnElPeriodo: number;
    rotacionPct: number; veteranosHoy: number; veteranosPct: number;
  };
  porMes: Array<{ mes: string; altas: number; bajas: number }>;
  impacto: Record<'reservas' | 'ventas' | 'alquileres', ImpactoPorTipo>;
  agentesConOperacion: { total: number; veteranos: number };
}

/** Impacto del CRM nuevo (Soporte › Analítica). */
export interface ImpactoCrm {
  corte: string;
  propiedadesPorMes: Array<{ mes: string; viejo: number; nuevo: number }>;
  promedios: {
    viejo12m: number; viejo6m: number; nuevo: number; mesesNuevo: number;
    mesEnCurso: { mes: string; aLaFecha: number; proyectado: number };
    variacionPct: number;
  };
  semanal: { viejo: number; nuevo: number; variacionPct: number; semanasNuevo: number };
  captaciones: {
    migradas: { total: number; convertidas: number };
    nuevas: { total: number; convertidas: number; tasaPct: number; medianaHorasAConvertir: number | null; descartesReales: number };
    porMes: Array<{ mes: string; creadas: number; convertidas: number }>;
    porOrigen: Array<{ origen: string; total: number; convertidas: number; tasaPct: number }>;
    estadoDeLasConvertidas: Record<string, number>;
  };
}

export const trackingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTrackingDashboard: build.query<DashboardData, { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/dashboard', params }),
      transformResponse: (res: { data: DashboardData }) => res.data,
    }),
    getBusinessMetrics: build.query<BusinessMetrics, { from: string; to: string; office?: string }>({
      query: (params) => ({ url: '/tracking/admin/business', params }),
      transformResponse: (res: { data: BusinessMetrics }) => res.data,
    }),
    getRotacionAgentes: build.query<RotacionAgentes, void>({
      query: () => '/tracking/admin/rotacion-agentes',
      transformResponse: (res: { data: RotacionAgentes }) => res.data,
    }),
    getImpactoCrm: build.query<ImpactoCrm, void>({
      query: () => '/tracking/admin/impacto-crm',
      transformResponse: (res: { data: ImpactoCrm }) => res.data,
    }),
    getBusinessOperations: build.query<BusinessOperations, { from: string; to: string; office?: string }>({
      query: (params) => ({ url: '/tracking/admin/business-operations', params }),
      transformResponse: (res: { data: BusinessOperations }) => res.data,
    }),
    getPropertiesMostViewed: build.query<PropertyView[], { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/properties-most-viewed', params }),
      transformResponse: (res: { data: PropertyView[] }) => res.data,
    }),
    getGustavoToolsTop: build.query<GustavoTool[], { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/gustavo-tools-top', params }),
      transformResponse: (res: { data: GustavoTool[] }) => res.data,
    }),
    getAgentActivity: build.query<AgentActivity[], { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/agent-activity', params }),
      transformResponse: (res: { data: AgentActivity[] }) => res.data,
    }),

    getHealthSummary: build.query<HealthSummary, { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/health-summary', params }),
      transformResponse: (res: { data: HealthSummary }) => res.data,
    }),

    getErrorsTop: build.query<ErrorsTop, { range?: RangeParam; limit?: number }>({
      query: (params) => ({ url: '/tracking/admin/errors-top', params }),
      transformResponse: (res: { data: ErrorsTop }) => res.data,
    }),

    getClientsSummary: build.query<ClientsSummary, { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/clients-summary', params }),
      transformResponse: (res: { data: ClientsSummary }) => res.data,
    }),

    getClientsFunnel: build.query<ClientsFunnel, { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/clients-funnel', params }),
      transformResponse: (res: { data: ClientsFunnel }) => res.data,
    }),

    getSearchesTop: build.query<SearchesTop, { range?: RangeParam; limit?: number }>({
      query: (params) => ({ url: '/tracking/admin/searches-top', params }),
      transformResponse: (res: { data: SearchesTop }) => res.data,
    }),

    getTrafficDistribution: build.query<TrafficDistribution, { range?: RangeParam }>({
      query: (params) => ({ url: '/tracking/admin/traffic-distribution', params }),
      transformResponse: (res: { data: TrafficDistribution }) => res.data,
    }),

    getAgentDetail: build.query<AgentDetail, { userId: string; range?: RangeParam }>({
      query: ({ userId, ...params }) => ({ url: `/tracking/admin/agent-activity/${userId}`, params }),
      transformResponse: (res: { data: AgentDetail }) => res.data,
    }),

    getServicesHealth: build.query<ServicesHealth, void>({
      query: () => ({ url: '/tracking/admin/services-health' }),
      transformResponse: (res: { data: ServicesHealth }) => res.data,
    }),

    // Estado de los deploys de Railway (widget del wallboard). Polling desde el widget.
    getDeployStatus: build.query<DeployStatus, void>({
      query: () => ({ url: '/tracking/admin/deploy-status' }),
      transformResponse: (res: { data: DeployStatus }) => res.data,
    }),

    getOnlineAgents: build.query<OnlineAgent[], void>({
      query: () => ({ url: '/tracking/admin/online-agents' }),
      transformResponse: (res: { data: OnlineAgent[] }) => res.data,
    }),

    // Visitantes web charlando con Gustavo ahora, con ciudad. Solo para el mapa de soporte:
    // la lista de "en línea" es de agentes.
    getOnlineVisitors: build.query<OnlineAgent[], void>({
      query: () => ({ url: '/tracking/admin/online-visitors' }),
      transformResponse: (res: { data: OnlineAgent[] }) => res.data.map((v) => ({ ...v, kind: 'visitor' as const })),
    }),

    // Clientes del Agente Virtual charlando ahora, puestos en la CASA que consultan.
    getOnlinePhylaxClients: build.query<OnlineAgent[], void>({
      query: () => ({ url: '/tracking/admin/online-phylax' }),
      transformResponse: (res: { data: OnlineAgent[] }) => res.data.map((v) => ({ ...v, kind: 'phylax' as const })),
    }),

    // Agentes en línea de la oficina del usuario (widget del admin de oficina).
    getOfficeOnlineAgents: build.query<OfficeOnlineAgents, void>({
      query: () => ({ url: '/tracking/office-online-agents' }),
      transformResponse: (res: { data: OfficeOnlineAgents }) => res.data,
    }),

    // Heartbeat de presencia real — lo dispara usePresence (pestaña visible + usuario activo).
    // Manda el modo de tema actual (claro/oscuro) para el medidor del wallboard de soporte.
    presencePing: build.mutation<{ ok: boolean }, { themeMode?: 'light' | 'dark' } | void>({
      query: (body) => ({ url: '/tracking/presence', method: 'POST', body: body ?? {} }),
    }),

    listErrors: build.query<ErrorsList, { range?: RangeParam; page?: number; limit?: number; search?: string; triageStatus?: ErrorTriageStatus | 'untriaged' }>({
      query: (params) => ({ url: '/tracking/admin/errors', params }),
      transformResponse: (res: { data: ErrorsList }) => res.data,
      providesTags: ['ErrorTriage'],
    }),

    setErrorTriage: build.mutation<ErrorTriage, { id: string; status: ErrorTriageStatus; note?: string }>({
      query: ({ id, status, note }) => ({
        url: `/tracking/admin/errors/${id}/triage`,
        method: 'PATCH',
        body: { status, note },
      }),
      transformResponse: (res: { data: ErrorTriage }) => res.data,
      invalidatesTags: ['ErrorTriage'],
    }),

    getClaudeExport: build.query<{ errorEventId: string; filename: string; markdown: string }, string>({
      query: (id) => ({ url: `/tracking/admin/errors/${id}/claude-export` }),
      transformResponse: (res: { data: { errorEventId: string; filename: string; markdown: string } }) => res.data,
    }),

    listErrorsGrouped: build.query<ErrorsGroupedList, { range?: RangeParam; page?: number; limit?: number; search?: string; triageStatus?: ErrorTriageStatus | 'untriaged'; severity?: ErrorSeverity | 'all' | 'actionable' }>({
      query: (params) => ({ url: '/tracking/admin/errors/grouped', params }),
      transformResponse: (res: { data: ErrorsGroupedList }) => res.data,
      providesTags: ['ErrorTriage'],
    }),

    setFingerprintTriage: build.mutation<{ affected: number }, { fingerprint: string; status: ErrorTriageStatus; note?: string }>({
      query: (body) => ({ url: '/tracking/admin/errors/triage-fingerprint', method: 'POST', body }),
      transformResponse: (res: { data: { affected: number } }) => res.data,
      invalidatesTags: ['ErrorTriage'],
    }),

    getActiveAlerts: build.query<ActiveAlerts, void>({
      query: () => ({ url: '/tracking/admin/alerts' }),
      transformResponse: (res: { data: ActiveAlerts }) => res.data,
    }),

    getWallboardSnapshot: build.query<WallboardSnapshot, void>({
      query: () => ({ url: '/tracking/admin/wallboard/snapshot' }),
      transformResponse: (res: { data: WallboardSnapshot }) => res.data,
    }),

    pingWallboard: build.mutation<{ ok: boolean; dispatched: number }, void>({
      query: () => ({ url: '/tracking/admin/wallboard/ping', method: 'POST' }),
      transformResponse: (res: { data: { ok: boolean; dispatched: number } }) => res.data,
    }),
  }),
});

export interface KpiComparison {
  vsHourPrev:  number | null;
  vsYesterday: number | null;
  vs7dAvg:     number | null;
}

export interface TopAgent {
  userId:     string;
  name:       string;
  email:      string | null;
  avatar:     string | null;
  eventCount: number;
  lastSeen:   string | null;
}

export interface WallboardSnapshot {
  serverTime: string;
  kpis: {
    onlineUsers:    number;
    reqLastMin:     number;
    errorsLastHour: number;
    bugsOpen:       number;
  };
  deltas?: {
    onlineUsers?:    number;
    reqLastMin?:     number;
    errorsLastHour?: number;
  };
  comparisons?: {
    onlineUsers:    KpiComparison;
    reqLastMin:     KpiComparison;
    errorsLastHour: KpiComparison;
    bugsOpen:       KpiComparison;
  };
  streak?: {
    days:           number;
    lastIncidentAt: string | null;
  };
  topAgent?: TopAgent | null;
  /** Top 3 agentes por eventos en las últimas 24h. */
  topAgents?: TopAgent[];
  /** Serie diaria "creado" — últimos 14 días (día calendario Uruguay, solo Imperium).
   *  El último elemento es HOY. */
  createdSeries?: Array<{
    date:             string;  // 'YYYY-MM-DD'
    customers:        number;
    properties:       number;
    sales:            number;
    rentals:          number;
    reservations:     number;
    reservationsSale: number;  // reservas de venta
    reservationsRent: number;  // reservas de alquiler (incl. temporal)
  }>;
  weekHeatmap?: Array<{ dow: number; hour: number; count: number }>;
  crmSections?: Array<{ section: string; count: number; last: string }>;
  services:        ServiceCheck[];
  servicesOverall: 'ok' | 'degraded' | 'down';
  alerts:          ActiveAlert[];
  topPaths:        Array<{ path: string;   count: number }>;
  topActions:      Array<{ action: string; count: number }>;
  sparkline:       Array<{ t: string; total: number; errors: number }>;
}

export interface ErrorGroup {
  fingerprint:           string;
  count:                 number;
  firstSeen:             string;
  lastSeen:              string;
  sampleId:              string;
  action:                string;
  label:                 string | null;
  path:                  string | null;
  endpoint:              string | null;
  status:                number | null;
  severity:              ErrorSeverity | null;
  affectedUsersCount:    number;
  affectedSessionsCount: number;
  triage: {
    status:    ErrorTriageStatus;
    note:      string | null;
    updatedAt: string;
  } | null;
}

export interface ErrorsGroupedList {
  range:      { from: string; to: string };
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      ErrorGroup[];
}

export interface ActiveAlert {
  kind:     string;
  severity: 'warning' | 'critical';
  title:    string;
  detail:   string;
  metric:   number;
}

export interface ActiveAlerts {
  alerts:    ActiveAlert[];
  checkedAt: string;
}

export type ErrorTriageStatus = 'open' | 'resolved' | 'dismissed' | 'assigned_claude';
export interface ErrorTriage {
  _id?:         string;
  errorEventId?: string;
  status:       ErrorTriageStatus;
  note:         string | null;
  resolvedAt:   string | null;
  exportedAt:   string | null;
  updatedAt:    string;
}

export interface ErrorEvent {
  _id:        string;
  type:       'error';
  category:   'agent' | 'client' | 'system';
  action:     string;
  label?:     string;
  path?:      string;
  sessionId:  string | null;
  userId:     string | null;
  userName:   string | null;
  userEmail:  string | null;
  userRole:   string | null;
  visitorId:  string | null;
  device:     string | null;
  userAgent:  string | null;
  meta:       Record<string, unknown>;
  createdAt:  string;
  triage:     ErrorTriage | null;
}

export interface ErrorsList {
  range:      { from: string; to: string };
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      ErrorEvent[];
}

export interface ServiceCheck {
  service:    'mongo' | 'openai' | 's3' | 'pusher' | 'resend' | 'mercadolibre' | 'vercel' | 'railway';
  category:   'core' | 'external';
  status:     'ok' | 'down' | 'degraded' | 'not_configured';
  latencyMs?: number;
  error?:     string;
  detail?:    string;
}

export interface ServicesHealth {
  checkedAt:  string;
  durationMs: number;
  overall:    'ok' | 'degraded' | 'down';
  services:   ServiceCheck[];
}

interface KPIWithDelta { current: number; previous: number }

export interface ClientsSummary {
  range: { from: string; to: string };
  visitors:       KPIWithDelta;
  propertyViews:  KPIWithDelta;
  chatsStarted:   KPIWithDelta;
  leadsCaptured:  KPIWithDelta;
  conversionRate: KPIWithDelta;
}

export interface FunnelStep {
  key:        string;
  label:      string;
  count:      number;
  pctOfPrev:  number;
  pctOfFirst: number;
}

export interface ClientsFunnel {
  range: { from: string; to: string };
  steps: FunnelStep[];
}

export interface SearchesTop {
  top:         Array<{ label: string; count: number; zeroHits: number }>;
  /** `lastAt` / `firstAt`: cuándo se buscó por última y primera vez (ISO). */
  zeroResults: Array<{ label: string; count: number; lastAt?: string; firstAt?: string }>;
  /** Desde cuándo hay datos. El log se reseteó el 2026-07-27 al corregir el tracking. */
  measuringSince?: string | null;
}

export interface TrafficDistribution {
  devices: Array<{ device: string; count: number }>;
  sources: Array<{ source: string; count: number }>;
}

export interface AgentDetail {
  range: { from: string; to: string };
  user: {
    _id: string; name: string; email: string;
    role: string; avatar?: string; office: string | null;
    memberSince: string;
  };
  summary: {
    total: number;
    firstSeen: string | null;
    lastSeen:  string | null;
    activeDays: number;
    errors: number;
  };
  byType:     Array<{ type: string; count: number }>;
  topPaths:   Array<{ path: string;   count: number }>;
  topActions: Array<{ action: string; count: number }>;
  topTools:   Array<{ tool: string;   count: number }>;
  hourly:     Array<{ hour: number;   count: number }>;
  timeline:   Array<{
    id: string; type: string; action: string;
    label?: string; path?: string;
    resourceType?: string; resourceId?: string;
    meta?: Record<string, unknown>;
    createdAt: string;
  }>;
  errors: Array<{ action: string; label?: string; path?: string; createdAt: string }>;
}

export interface HealthSummary {
  range: { from: string; to: string };
  events:  { current: number; previous: number };
  errors:  { current: number; previous: number };
  agents:  { current: number; previous: number };
  gustavo: { conversations: number; toolCalls: number; errorRate: number };
}

export interface ErrorsTop {
  byPath:   Array<{ path: string;   count: number }>;
  byAction: Array<{ action: string; count: number }>;
}

/** Dispositivo del último heartbeat: dice si el agente está en la calle o sentado. */
export interface PresenceDevice {
  kind:   'mobile' | 'tablet' | 'desktop';
  label?: string;   // "iPhone", "Android", "Windows"… para el tooltip
}

export interface OnlineAgent {
  _id:        string;
  /** 'visitor' = visitante de la web charlando con Gustavo; 'phylax' = cliente del Agente
   *  Virtual, ubicado en la CASA que consulta (no donde está él); sin él, es un agente. */
  kind?:      'agent' | 'visitor' | 'phylax';
  /** Solo `kind: 'phylax'`: la propiedad de la que está hablando. */
  propiedad?: { propertyId: string; title?: string };
  /** Solo `kind: 'visitor'`: le está escribiendo a Gustavo (true) o solo navega (false). */
  charlando?: boolean;
  /** Solo `kind: 'visitor'` navegando: la última página que abrió. */
  path?:      string;
  name:       string;
  avatar?:    string;
  role?:      string;
  office?:    string;
  device?:    PresenceDevice;
  lastSeenAt: string;
  themeMode?: 'light' | 'dark';   // último modo de tema reportado por presencia
  gustavoWa?: boolean;            // activo por WhatsApp hablando con Gustavo (no por el CRM)
  /** Desde dónde late: CIUDAD por IP del último heartbeat (nunca la IP). Para el mapa. */
  location?:  { city?: string; region?: string; country: string; lat: number; lng: number; at?: string };
  /** Para la tarjeta del mapa: de qué inmobiliaria es y de qué plataforma/país. */
  officeName?: string;
  plataforma?: 'imperium' | 'booster';
  pais?:       string;
}

export interface DeployService {
  key:           string;
  label:         string;
  status:        string;   // crudo de Railway (BUILDING/SUCCESS/…)
  /** `unreachable` = no se pudo preguntar, distinto de `idle` (nada en curso). */
  state:         'building' | 'success' | 'failed' | 'idle' | 'unreachable';
  /** Por qué no se pudo consultar. Solo con `unreachable`. */
  error?:        string;
  createdAt:     string | null;
  commitHash:    string | null;
  commitMessage: string | null;
  branch:        string | null;
}
export interface DeployStatus {
  configured: boolean;
  services:   DeployService[];
}

export interface OfficeOnlineAgent {
  _id:        string;
  name:       string;
  avatar?:    string;
  device?:    PresenceDevice;
  lastSeenAt: string;
}
export interface OfficeOnlineAgents {
  total:  number;
  agents: OfficeOnlineAgent[];
}

/** Métricas de negocio (tab "Negocio" de Analítica). Volumen en USD y UYU por separado. */
export interface CurAmount { USD: number; UYU: number }
export interface BusinessMetrics {
  range: { from: string; to: string; days: number };
  kpis: {
    sales:        { count: number; prevCount: number };
    rentals:      { count: number; prevCount: number };
    reservations: { count: number; prevCount: number };
    salesVolume:   { current: CurAmount; prev: CurAmount };
    rentalsVolume: { current: CurAmount; prev: CurAmount };
    avgTicket:    { USD: number | null; UYU: number | null };
    avgDaysOnMarket: number | null;
  };
  series: Array<{
    date: string; sales: number; rentals: number; reservations: number;
    salesUSD: number; salesUYU: number; rentUSD: number; rentUYU: number; reservUSD: number; reservUYU: number;
    salesCommUSD: number; salesCommUYU: number; rentCommUSD: number; rentCommUYU: number; reservCommUSD: number; reservCommUYU: number;
  }>;
  pipeline: { draft: number; active: number; reserved: number; sold: number; rented: number; inactive: number };
  funnel: {
    created: number; reserved: number; closed: number;
    convReservedPct: number | null; convClosedPct: number | null; convTotalPct: number | null;
  };
  byOffice: BusinessRankRow[];
  byAgent:  BusinessRankRow[];
}
export interface BusinessRankRow {
  id: string; name?: string; sales: number; rentals: number; count: number; volumeUSD: number; volumeUYU: number;
}

/** Tiempos y ganancias por operación (reparto de comisión agente/oficina/corporativo). */
export interface BusinessOpRow {
  cat: 'venta' | 'renta' | 'reserva';
  propertyId: string; office: string; officeName: string; agent: string; months: number | null;
  agUSD: number; agUYU: number; ofUSD: number; ofUYU: number; coUSD: number; coUYU: number;
}
export interface BusinessOperations {
  kpis: { count: number; ventas: number; rentas: number; reservas: number; avgMonths: number | null; over6: number; over6Pct: number | null };
  timeBuckets: { fast: number; mid: number; slow: number };
  earnings: { agent: CurAmount; office: CurAmount; corporate: CurAmount };
  operations: BusinessOpRow[];
}

export const {
  useGetBusinessOperationsQuery,
  useGetImpactoCrmQuery,
  useGetRotacionAgentesQuery,
  useGetBusinessMetricsQuery,
  useGetTrackingDashboardQuery,
  useGetPropertiesMostViewedQuery,
  useGetGustavoToolsTopQuery,
  useGetAgentActivityQuery,
  useGetHealthSummaryQuery,
  useGetErrorsTopQuery,
  useGetClientsSummaryQuery,
  useGetClientsFunnelQuery,
  useGetSearchesTopQuery,
  useGetTrafficDistributionQuery,
  useGetAgentDetailQuery,
  useGetServicesHealthQuery,
  useGetDeployStatusQuery,
  useListErrorsQuery,
  useSetErrorTriageMutation,
  useLazyGetClaudeExportQuery,
  useListErrorsGroupedQuery,
  useSetFingerprintTriageMutation,
  useGetActiveAlertsQuery,
  useGetWallboardSnapshotQuery,
  usePingWallboardMutation,
  useGetOnlineAgentsQuery,
  useGetOnlineVisitorsQuery,
  useGetOnlinePhylaxClientsQuery,
  useGetOfficeOnlineAgentsQuery,
  usePresencePingMutation,
} = trackingApi;
